import { Box, Typography, Card, CardContent, IconButton, Grid, Theme } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Project } from './types';
import STLViewer from 'components/viewer/STLViewer';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRef } from 'react';
import { Theme as MuiTheme } from '@mui/material/styles';

interface ProjectCardProps {
  project: Project;
  isOwnProfile: boolean;
  onUpload: (files: File[]) => void;
  onDelete: () => void;
  onSelectAsset: (index: number) => void;
  onDeleteFile: (projectId: string, fileId: string) => void;
}

export default function ProjectCard({
  project,
  isOwnProfile,
  onUpload,
  onDelete,
  onSelectAsset,
  onDeleteFile,
}: ProjectCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop: (item: { files: File[] }) => {
        const files = Array.from(item.files).filter(file => {
          const isSTL =
            file.type === 'model/stl' ||
            file.type === 'application/octet-stream' ||
            file.name.toLowerCase().endsWith('.stl');
          const isImage = file.type.startsWith('image/');
          return isSTL || isImage;
        });
        if (files.length) {
          onUpload(files);
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onUpload]
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;
    const files = Array.from(event.target.files).filter(file => {
      const isSTL =
        file.type === 'model/stl' ||
        file.type === 'application/octet-stream' ||
        file.name.toLowerCase().endsWith('.stl');
      const isImage = file.type.startsWith('image/');
      return isSTL || isImage;
    });
    if (files.length) {
      onUpload(files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isDragActive = isOver && canDrop;

  const UploadArea = () => (
    <Box
      onClick={() => fileInputRef.current?.click()}
      sx={{
        border: '2px dashed',
        borderColor: (theme: Theme) =>
          isDragActive ? theme.palette.primary.main : theme.palette.divider,
        borderRadius: 1,
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          borderColor: (theme: Theme) => theme.palette.primary.main,
          bgcolor: 'action.hover',
        },
      }}
    >
      <CloudUploadIcon
        sx={{
          fontSize: 48,
          mb: 2,
          color: 'text.secondary',
        }}
      />
      <Typography variant="body1" gutterBottom>
        {isDragActive
          ? 'Drop files here'
          : 'Drag and drop STL or image files here, or click to select'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Supported formats: STL, JPG, PNG, GIF
      </Typography>
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3">
              {project.title}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {project.description}
            </Typography>
          </Box>
          {isOwnProfile && (
            <Box>
              <IconButton size="small" onClick={onDelete}>
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box
          ref={isOwnProfile ? drop : undefined}
          sx={{
            position: 'relative',
            minHeight: 200,
            ...(isDragActive && {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: (theme: MuiTheme) => theme.palette.primary.main,
                opacity: 0.1,
                zIndex: 1,
                pointerEvents: 'none',
              },
            }),
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".stl,image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
          />

          {project.files.length > 0 ? (
            <Grid container spacing={2}>
              {project.files.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} key={file.id}>
                  <Box
                    onClick={() => onSelectAsset(index)}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1/1',
                      width: '100%',
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                  >
                    {file.type === 'STL' ? (
                      <STLViewer url={file.url} />
                    ) : (
                      <Box
                        component="img"
                        src={file.url}
                        alt={file.name}
                        {...({} as React.ComponentProps<'img'>)}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    {isOwnProfile && (
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          onDeleteFile(project.id, file.id);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'background.paper',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                    {file.name}
                  </Typography>
                </Grid>
              ))}
              {isOwnProfile && (
                <Grid item xs={12} sm={6} md={4}>
                  <UploadArea />
                </Grid>
              )}
            </Grid>
          ) : isOwnProfile ? (
            <UploadArea />
          ) : (
            <Typography color="text.secondary" align="center" py={4}>
              No files uploaded yet
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
